import { useState, useEffect } from 'react';
import Modal from 'react-modal';
import checkIcon from '../images/checked.png';
import logo from '../images/Logo-square.png'; // Essa logo deverá ser a logo do usuário
import '../css/style.css';
import '../css/Config_popup.css';
import api from '../service/api';
import { auth } from '../service/firebase';
import { verifyBeforeUpdateEmail } from 'firebase/auth';

Modal.setAppElement('#root');

function ConfigPopup({ isOpen, onRequestClose, userData, setUserData }) {

    const [name, setName] = useState(userData.name);
    const [email, setEmail] = useState(userData.email);
    const [cref, setCref] = useState(userData.cref);
    const [colorStyle, setColorStyle] = useState(userData.colorStyle);
    const [colorTheme, setColorTheme] = useState('');
    const [foto, setFoto] = useState('');       // preview (URL do R2 ou dataURL local)
    const [fotoFile, setFotoFile] = useState(null); // arquivo a ser enviado ao R2

    const [mensagemFoto, setMensagemFoto] = useState("Adicione uma foto até 2MB");
    const [mensagemErro, setMensagemErro] = useState("");
    const [mensagemEmail, setMensagemEmail] = useState("");
    const [configSucesso, setConfigSucesso] = useState(false);

    useEffect(() => {
        if (isOpen) {
            setName(userData.name || '');
            setEmail(userData.email || '');
            setCref(userData.cref || '');
            setColorStyle(userData.colorStyle || 'theme1');
            setColorTheme('');
            setFoto(userData.photo || ''); // mostra a foto atual do personal, se houver
            setFotoFile(null);

            setMensagemFoto(userData.photo ? "Imagem carregada" : "Adicione uma foto até 2MB");
            setMensagemErro("");
            setMensagemEmail("");
            setConfigSucesso(false);
        }
    }, [isOpen]);

    // Troca de e-mail pelo Firebase Auth: envia um link de confirmação para o NOVO endereço e só
    // altera o e-mail de login depois do clique. O Firebase também avisa o endereço antigo, com um
    // link para reverter a troca. Até a confirmação, o e-mail de acesso continua o atual.
    const solicitarTrocaDeEmail = async (novoEmail) => {
        const usuario = auth.currentUser;
        if (!usuario) {
            throw new Error('Sessão do Firebase não encontrada. Saia do sistema e entre novamente para trocar o e-mail.');
        }
        try {
            await verifyBeforeUpdateEmail(usuario, novoEmail);
        } catch (erro) {
            const mensagens = {
                'auth/requires-recent-login': 'Por segurança, a troca de e-mail exige um login recente. Saia do sistema, entre novamente e repita a alteração.',
                'auth/invalid-email': 'O novo e-mail informado não é válido.',
                'auth/email-already-in-use': 'Este e-mail já está em uso por outra conta.',
                'auth/too-many-requests': 'Muitas tentativas em sequência. Aguarde alguns minutos e tente de novo.',
            };
            throw new Error(mensagens[erro?.code] || 'Não foi possível solicitar a troca de e-mail. Tente novamente.');
        }
    };

    // Troca a foto
    const handleImageChange = (event) => {
        const file = event.target.files[0];

        if (file) {
            if (file.size > 2 * 1024 * 1024) { // 2MB em bytes
                setMensagemFoto("A imagem excedeu o limite de 2MB, selecione outra!");
                setFoto(null);
                setFotoFile(null);
                return;
            }

            setFotoFile(file); // guarda o arquivo para enviar ao R2
            const reader = new FileReader();
            reader.onload = () => {
                setFoto(reader.result); // apenas para pré-visualização
                setMensagemFoto(file.name); // Exibe o nome do arquivo carregado
            };
            reader.readAsDataURL(file);
        } else {
            setFoto(null);
            setFotoFile(null);
            setMensagemFoto("Adicione uma foto até 2MB");
        }
    };

    const changeColorStyle = (theme) => {
        document.documentElement.setAttribute('data-color-style', theme);
    };

    const changeTheme = (theme) => {
        document.documentElement.setAttribute('data-theme', theme);
    };

    async function handleConfig(e) {
        e.preventDefault();
        setMensagemErro("");
        setMensagemEmail("");

        const emailAtual = (userData.email || '').trim();
        const emailNovo = (email || '').trim();
        const trocouEmail = emailNovo !== '' && emailNovo.toLowerCase() !== emailAtual.toLowerCase();

        if (trocouEmail) {
            try {
                await solicitarTrocaDeEmail(emailNovo);
                setMensagemEmail(`Enviamos um link de confirmação para ${emailNovo}. O e-mail de acesso só muda depois que você clicar nele — até lá, continue entrando com ${emailAtual}. Depois de confirmar, entre novamente no sistema.`);
            } catch (erro) {
                setMensagemErro(erro.message);
                return;
            }
        }

        // O e-mail gravado no cadastro é sempre o de login vigente; ele passa a ser o novo
        // automaticamente no próximo login, depois que a confirmação for concluída.
        const data = {
            name,
            email: emailAtual,
            cref,
            colorStyle,
            //colorTheme
        };

        try {
            // Configurar a rota
            const response = await api.post(`/api/user/config/${userData.uid}`, data, { withCredentials: true });

            if (response.status === 200) {
                const updatedUserData = { ...userData, ...data };

                // Envia a foto do personal ao R2 (se selecionou uma nova) e guarda a URL.
                if (fotoFile) {
                    try {
                        const formData = new FormData();
                        formData.append('foto', fotoFile);
                        const upload = await api.post('/api/user/foto', formData, { withCredentials: true });
                        updatedUserData.photo = upload.data.url;
                    } catch (uploadErr) {
                        setMensagemErro('Dados salvos, mas houve um erro ao enviar a foto.');
                        return;
                    }
                }

                setUserData(updatedUserData);
                localStorage.setItem('@AppAcademia:user', JSON.stringify(updatedUserData));
                setConfigSucesso(true);

                changeColorStyle(colorStyle);
                if (colorTheme) {
                    changeTheme(colorTheme);
                }

                // Com troca de e-mail pendente, o aviso fica na tela até o usuário fechar.
                if (!trocouEmail) {
                    setTimeout(() => {
                        onRequestClose();
                    }, 2000);
                }
            }

        } catch (err) {
            // Falta adicionar caso ocorra algum erro do Backend
            // console.log(err);
            setMensagemErro('Erro ao atualizar dados, tente novamente!');
        };

    };

    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={onRequestClose}
            contentLabel="Configs"
            className="config-modal"
            overlayClassName="cadastro-overlay"
        >
            <div className="cadastro-content" id="cadastro-form-container" style={{ display: configSucesso ? "none" : "block" }}>
                <p className="config-title">Configurações</p>
                <div className="config-center">
                    <div className="config-left">
                        <div className="form-config">
                            <div className="form-label-input">
                                <label htmlFor="nome">Nome de usuário</label>
                                <input id="nome" type="text" placeholder="" value={name} onChange={(e) => setName(e.target.value)} />
                            </div>
                            <div className="form-label-input">
                                <label htmlFor="email">E-mail</label>
                                <input id="email" type="email" placeholder="" value={email} onChange={(e) => setEmail(e.target.value)} />
                                <p className="config-dica">
                                    Ao alterar o e-mail, enviamos um link de confirmação para o novo endereço. A troca só é concluída após a confirmação.
                                </p>
                            </div>
                            <div className="form-label-input">
                                <label htmlFor="cref">CREF</label>
                                <input id="cref" type="text" placeholder="" value={cref} onChange={(e) => setCref(e.target.value)} />
                            </div>
                            <div className="form-2-columns">
                                <div className="form-label-input">
                                    <label htmlFor="colorStyle">Cores do sistema</label>
                                    <select id="colorStyle" value={colorStyle} onChange={(e) => setColorStyle(e.target.value)}>
                                        <option value="theme1">Laranja</option>
                                        <option value="theme2">Azul</option>
                                        <option value="theme3">Rosa</option>
                                        <option value="theme4">Verde</option>
                                        <option value="theme5">Roxo</option>
                                    </select>
                                </div>

                                {/* Tema claro e escuro - A programar
                                <div className="form-label-input">
                                    <label htmlFor="colorTheme">Tema</label>
                                    <select id="colorTheme" value={colorTheme} onChange={(e) => setColorTheme(e.target.value)}>
                                        <option value="dark-theme">Escuro</option>
                                        <option value="light-theme">Claro</option>
                                    </select>
                                </div>
                                */}
                            </div>
                        </div>
                    </div>
                    <div className="config-right">
                        <input type="file" accept="image/*" onChange={handleImageChange} id="photo-upload-input" style={{ display: 'none' }} />
                        <label htmlFor="photo-upload-input">
                            {foto ? (
                                <img className="foto-config" src={foto} alt="Foto do Aluno" />
                            ) : (
                                <img className="foto-config" src={logo} alt="Foto Padrão" />
                            )}
                        </label>
                        <p className="config-foto-title">{mensagemFoto}</p>
                    </div>
                </div>
                {mensagemEmail && <p className="config-aviso-email">{mensagemEmail}</p>}
                {mensagemErro && <p className="error-message error-message-config">{mensagemErro}</p>}
                <button onClick={handleConfig} className="config-button">Alterar informações</button>

            </div>
            <div className="config-content text-center" id="config-sucesso-container" style={{ display: configSucesso ? "block" : "none" }}>
                <p className="config-title">Alterações realizadas com sucesso!</p>
                <img className="check-icon" src={checkIcon} alt="Checked" />
            </div>

        </Modal>
    );
}

export default ConfigPopup;
